// Runtime string lookup. Every user-visible string goes through t()
// (EDUCATION-DESIGN.md §7: no hardcoded text). Curated translations only —
// nothing is machine-translated at runtime.

import en from './en.json';
import es from './es.json';
import type { Locale } from '../core/types';

export type StringTable = Record<string, string>;

export const TABLES: Record<Locale, StringTable> = { en, es };

/** Browser preference, defaulting to English for anything unexpected. */
export function detectLocale(): Locale {
  const nav = typeof navigator !== 'undefined' ? navigator.language : 'en';
  return nav?.toLowerCase().startsWith('es') ? 'es' : 'en';
}

let current: Locale = 'en';
const listeners = new Set<(locale: Locale) => void>();

export function getLocale(): Locale {
  return current;
}

export function setLocale(locale: Locale): void {
  if (locale === current) return;
  current = locale;
  for (const listener of [...listeners]) listener(locale);
}

/** Re-render hook for panels built before a language change. */
export function onLocaleChange(listener: (locale: Locale) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Looks up `key`, substituting `{name}` placeholders. A missing key returns the
 * key itself — loud in the UI, so gaps get noticed instead of shipping blank.
 */
export function t(key: string, params?: Record<string, string | number>): string {
  const table = TABLES[current];
  const raw = table[key] ?? TABLES.en[key];
  if (raw === undefined) {
    console.warn(`[i18n] missing key: ${key}`);
    return key;
  }
  if (!params) return raw;
  return raw.replace(/\{(\w+)\}/g, (match, name: string) =>
    params[name] !== undefined ? String(params[name]) : match,
  );
}

/** Picks the right side of an already-bilingual data field (items, facts). */
export function pick(pair: { en: string; es: string }): string {
  return pair[current];
}
